/**
 * @fileoverview User context convenience hooks
 * Separated from Provider files so those files only export React components,
 * which is required for Vite/React Fast Refresh to work without full reloads.
 */

import { useContext } from 'react';
import type { UserTier } from '../../types/user';

// ---- Lazy imports of context objects to avoid circular deps ----
// The actual contexts are created in the Provider files.
// We import them here so hooks can useContext() without being co-located.

// UserProfileContext
import { UserProfileContext } from './UserProfileContext';

// UserLimitsContext  
import { UserLimitsContext } from './UserLimitsContext';

// UserProgressContext
import { UserProgressContext } from './UserProgressContext.shared';

// --------------- Profile Hooks ---------------

export const useUserProfile = () => {
    const context = useContext(UserProfileContext);
    if (context === undefined) {
        throw new Error('useUserProfile must be used within a UserProfileProvider');
    }
    return context;
};

export const useUserTier = (): UserTier => {
    const { tier } = useUserProfile();
    return tier;
};

export const useUserName = (): string | null => {
    const { profile } = useUserProfile();
    return profile?.email || null;
};

export const useUserEmail = (): string | null => {
    const { profile } = useUserProfile();
    return profile?.email || null;
};

// --------------- Limits Hooks ---------------

export const useUserLimits = () => {
    const context = useContext(UserLimitsContext);
    if (context === undefined) {
        throw new Error('useUserLimits must be used within a UserLimitsProvider');
    }
    return context;
};

export const useCanCreateStrategy = (): boolean => {
    const { canCreateStrategy } = useUserLimits();
    return canCreateStrategy;
};

export const useCanSaveBacktest = (): boolean => {
    const { canSaveBacktest } = useUserLimits();
    return canSaveBacktest;
};

export const useCanShareStrategies = (): boolean => {
    const { canShareStrategies } = useUserLimits();
    return canShareStrategies;
};

export const useHasAdvancedAnalytics = (): boolean => {
    const { hasAdvancedAnalytics } = useUserLimits();
    return hasAdvancedAnalytics;
};

export const useBuiltInStrategyAccess = () => {
    const { hasAccessToBuiltInStrategy, getAccessibleBuiltInStrategies } = useUserLimits();
    return {
        hasAccess: hasAccessToBuiltInStrategy,
        getAccessible: getAccessibleBuiltInStrategies
    };
};

// --------------- Progress Hooks ---------------

export const useUserProgress = () => {
    const context = useContext(UserProgressContext);
    if (context === undefined) {
        throw new Error('useUserProgress must be used within a UserProgressProvider');
    }
    return context;
};

export const useStrategyProgress = () => {
    const { strategyProgress } = useUserProgress();
    return strategyProgress;
};

export const useBacktestProgress = () => {
    const { backtestProgress } = useUserProgress();
    return backtestProgress;
};

export const useProgressActions = () => {
    const { incrementStrategyCount, incrementBacktestCount } = useUserProgress();
    return {
        incrementStrategy: incrementStrategyCount,
        incrementBacktest: incrementBacktestCount
    };
};

export const useUsageStatus = () => {
    const { hasActiveUsage, isApproachingLimits } = useUserProgress();
    return {
        hasActiveUsage,
        isApproachingLimits
    };
};
